import { useEffect, useState } from 'react';
import { useResource } from '../../lib/useResource.js';
import { post, put } from '../../lib/api.js';
import { formatBRL } from '../../lib/format.js';
import {
  Alert,
  Badge,
  Card,
  ErrorBox,
  Field,
  useSubmit,
  useToast,
} from '../../components/ui.js';
import { Tabela } from '../../components/Tabela.js';
import { useSession } from '../../lib/session.js';

interface ConfiguracaoDaIa {
  ativo: boolean;
  nome: string;
  tom: string;
  saudacao: string;
  faq: { pergunta: string; resposta: string }[];
  criteriosDeEncaminhamento: string[];
  horarioHumano: string;
  respostaDeAusencia: string;
  maxChamadasDeFerramenta: number;
  timeoutSegundos: number;
  maxRespostasConsecutivas: number;
  turnoCentavos: number;
  diarioCentavos: number | null;
  alertaEmPorcento: number;
  versao: number;
}

interface Orcamento {
  mensalCentavos: number;
  gastoNoMesCentavos: number;
  reservadoCentavos: number;
  restanteCentavos: number;
  porcentoUsado: number;
  emAlerta: boolean;
  estourado: boolean;
}

interface Ajustes {
  config: ConfiguracaoDaIa;
  provider: { nome: string; modelo: string } | null;
  orcamento: Orcamento;
  ferramentas: { nome: string; descricao: string; escreve: boolean }[];
  pendenciasDeConfirmacao: { item: string; onde: string }[];
}

interface ResultadoDoTeste {
  resultado: {
    desfecho: string;
    detalhe?: string;
    textoEnviado?: string;
    chamadasDeFerramenta: { nome: string; ok: boolean }[];
    custoCentavos: number;
  };
  historico: { direction: string; body: string | null }[];
  aviso: string;
}

const DESFECHO: Record<string, { rotulo: string; tom: 'neutral' | 'attention' | 'critical' | 'ok' }> = {
  respondeu: { rotulo: 'respondeu', tom: 'ok' },
  encaminhou_para_humano: { rotulo: 'encaminhou para uma pessoa', tom: 'attention' },
  sem_resposta: { rotulo: 'não soube responder', tom: 'attention' },
  ia_desligada: { rotulo: 'assistente desligado', tom: 'neutral' },
  conversa_nao_elegivel: { rotulo: 'conversa não elegível', tom: 'neutral' },
  orcamento_esgotado: { rotulo: 'teto de gasto atingido', tom: 'critical' },
  falha_da_ia: { rotulo: 'falha da IA', tom: 'critical' },
  resposta_recusada: { rotulo: 'resposta recusada na conferência', tom: 'critical' },
};

export function RegrasIA() {
  const { can } = useSession();
  const toast = useToast();
  const ajustes = useResource<Ajustes>('/api/ai/settings');
  const [config, setConfig] = useState<ConfiguracaoDaIa | null>(null);
  const [motivo, setMotivo] = useState('');

  useEffect(() => {
    if (ajustes.data?.config) setConfig(ajustes.data.config);
  }, [ajustes.data]);

  const salvar = useSubmit(async () => {
    if (!config) return;
    await put('/api/ai/settings', { ...config, motivo });
    setMotivo('');
    ajustes.reload();
    toast('Configuração do assistente salva.');
  });

  if (!config) {
    return (
      <Card>
        <ErrorBox error={ajustes.error} />
        <p className="muted">Carregando…</p>
      </Card>
    );
  }

  const semProvedor = !ajustes.data?.provider;
  const editavel = can('ai.config.write');

  return (
    <>
      <Card title="Situação do assistente">
        <ErrorBox error={ajustes.error ?? salvar.error} />

        {semProvedor ? (
          <Alert tone="info" title="Nenhum provedor de IA configurado">
            Defina <code>AI_PROVIDER</code>, <code>AI_API_KEY</code> e <code>AI_MODEL</code> no
            arquivo <code>.env</code> e reinicie os serviços. Sem isso o assistente não liga — e o
            sistema continua funcionando sem ele.
          </Alert>
        ) : (
          <p>
            Provedor: <strong>{ajustes.data!.provider!.nome}</strong> · modelo{' '}
            <strong>{ajustes.data!.provider!.modelo}</strong>{' '}
            <Badge tone={config.ativo ? 'ok' : 'neutral'}>
              {config.ativo ? 'ligado' : 'desligado'}
            </Badge>{' '}
            <span className="muted small">versão {config.versao} da configuração</span>
          </p>
        )}

        <label className="checkbox">
          <input
            type="checkbox"
            disabled={!editavel || semProvedor}
            checked={config.ativo}
            onChange={(e) => setConfig({ ...config, ativo: e.target.checked })}
          />
          <span>
            <strong>Ligar o assistente</strong>
            <div className="small muted">
              Desligado, toda mensagem vai direto para atendimento humano. Nada mais muda.
            </div>
          </span>
        </label>
      </Card>

      <Card title="Gasto de IA">
        <div className="grid cols-4">
          <div className="indicator">
            <div className="value">{formatBRL(ajustes.data!.orcamento.mensalCentavos)}</div>
            <div className="label">Teto do mês</div>
          </div>
          <div className="indicator">
            <div className="value">{formatBRL(ajustes.data!.orcamento.gastoNoMesCentavos)}</div>
            <div className="label">Gasto</div>
          </div>
          <div className="indicator">
            <div className="value">{formatBRL(ajustes.data!.orcamento.restanteCentavos)}</div>
            <div className="label">Restante</div>
          </div>
          <div className="indicator">
            <div className="value">{ajustes.data!.orcamento.porcentoUsado}%</div>
            <div className="label">Usado</div>
          </div>
        </div>
        {ajustes.data!.orcamento.estourado ? (
          <Alert tone="error" title="Teto atingido">
            O assistente parou de responder e as conversas vão para atendimento humano. Cadastro,
            catálogo, agenda e lembretes continuam funcionando normalmente.
          </Alert>
        ) : ajustes.data!.orcamento.emAlerta ? (
          <Alert tone="warning" title="Passou do limite de alerta" />
        ) : null}
      </Card>

      <Card title="Como o assistente fala">
        <div className="field-row">
          <Field label="Nome do assistente">
            <input
              type="text"
              value={config.nome}
              disabled={!editavel}
              onChange={(e) => setConfig({ ...config, nome: e.target.value })}
            />
          </Field>
          <Field label="Tom">
            <input
              type="text"
              value={config.tom}
              disabled={!editavel}
              onChange={(e) => setConfig({ ...config, tom: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Saudação">
          <input
            type="text"
            value={config.saudacao}
            disabled={!editavel}
            onChange={(e) => setConfig({ ...config, saudacao: e.target.value })}
          />
        </Field>
        <div className="field-row">
          <Field label="Horário de atendimento humano">
            <input
              type="text"
              value={config.horarioHumano}
              disabled={!editavel}
              onChange={(e) => setConfig({ ...config, horarioHumano: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Resposta quando não há ninguém">
          <textarea
            value={config.respostaDeAusencia}
            disabled={!editavel}
            onChange={(e) => setConfig({ ...config, respostaDeAusencia: e.target.value })}
          />
        </Field>
      </Card>

      <Card
        title="Perguntas frequentes"
        description="Conteúdo textual aprovado. Preço fica no catálogo: não escreva valor aqui."
      >
        {config.faq.map((item, i) => (
          <div
            key={i}
            style={{ border: '1px solid var(--gl-border)', borderRadius: 6, padding: 12, marginBottom: 10 }}
          >
            <Field label={`Pergunta ${i + 1}`}>
              <input
                type="text"
                value={item.pergunta}
                disabled={!editavel}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    faq: config.faq.map((f, j) => (j === i ? { ...f, pergunta: e.target.value } : f)),
                  })
                }
              />
            </Field>
            <Field label="Resposta aprovada">
              <textarea
                value={item.resposta}
                disabled={!editavel}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    faq: config.faq.map((f, j) => (j === i ? { ...f, resposta: e.target.value } : f)),
                  })
                }
              />
            </Field>
            {editavel ? (
              <button
                type="button"
                className="small danger"
                onClick={() => setConfig({ ...config, faq: config.faq.filter((_, j) => j !== i) })}
              >
                Remover
              </button>
            ) : null}
          </div>
        ))}
        {editavel ? (
          <button
            type="button"
            onClick={() =>
              setConfig({ ...config, faq: [...config.faq, { pergunta: '', resposta: '' }] })
            }
          >
            Adicionar pergunta
          </button>
        ) : null}
      </Card>

      <Card title="Quando chamar uma pessoa">
        <Field label="Critérios" hint="Um por linha.">
          <textarea
            rows={7}
            disabled={!editavel}
            value={config.criteriosDeEncaminhamento.join('\n')}
            onChange={(e) =>
              setConfig({
                ...config,
                criteriosDeEncaminhamento: e.target.value.split('\n').filter((l) => l.trim()),
              })
            }
          />
        </Field>
      </Card>

      <Card title="Limites">
        <div className="field-row">
          <Field label="Chamadas de ferramenta por turno">
            <input
              type="number"
              min={1}
              max={12}
              value={config.maxChamadasDeFerramenta}
              disabled={!editavel}
              onChange={(e) =>
                setConfig({ ...config, maxChamadasDeFerramenta: Number(e.target.value) })
              }
            />
          </Field>
          <Field label="Tempo máximo por turno (segundos)">
            <input
              type="number"
              min={5}
              max={120}
              value={config.timeoutSegundos}
              disabled={!editavel}
              onChange={(e) => setConfig({ ...config, timeoutSegundos: Number(e.target.value) })}
            />
          </Field>
          <Field label="Respostas seguidas antes de chamar alguém">
            <input
              type="number"
              min={1}
              max={20}
              value={config.maxRespostasConsecutivas}
              disabled={!editavel}
              onChange={(e) =>
                setConfig({ ...config, maxRespostasConsecutivas: Number(e.target.value) })
              }
            />
          </Field>
        </div>
        <div className="field-row">
          <Field label="Alerta em (% do teto)">
            <input
              type="number"
              min={10}
              max={100}
              value={config.alertaEmPorcento}
              disabled={!editavel}
              onChange={(e) => setConfig({ ...config, alertaEmPorcento: Number(e.target.value) })}
            />
          </Field>
        </div>
      </Card>

      <Card
        title="O que o assistente pode fazer"
        description="Estas são as únicas ações possíveis. Ele não altera preço, política, credencial nem permissão."
        tight
      >
        <Tabela
          linhas={ajustes.data?.ferramentas ?? []}
          chave={(f) => f.nome}
          vazio="Nenhuma ferramenta."
          colunas={[
            { chave: 'nome', rotulo: 'Ação', tipo: 'principal', render: (f) => <code>{f.nome}</code> },
            { chave: 'desc', rotulo: 'O que faz', render: (f) => <span className="small">{f.descricao}</span> },
            {
              chave: 'grava',
              rotulo: 'Grava',
              render: (f) => (f.escreve ? <Badge tone="attention">grava</Badge> : 'só consulta'),
            },
          ]}
        />
      </Card>

      {ajustes.data?.pendenciasDeConfirmacao?.length ? (
        <Card
          title="Itens do provedor ainda por confirmar"
          description="Pendência P10: preço e formato precisam ser conferidos antes de ligar em produção."
          tight
        >
          <Tabela
            linhas={ajustes.data.pendenciasDeConfirmacao}
            chave={(p) => p.item}
            vazio="Nada pendente."
            colunas={[
              { chave: 'item', rotulo: 'Item', tipo: 'principal', render: (p) => p.item },
              { chave: 'onde', rotulo: 'Onde conferir', render: (p) => p.onde },
            ]}
          />
        </Card>
      ) : null}

      {editavel ? (
        <Card title="Salvar">
          <form onSubmit={salvar.submit}>
            <Field label="Motivo da mudança" required hint="Fica registrado na auditoria, com a versão.">
              <input
                type="text"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                required
                minLength={3}
              />
            </Field>
            <button type="submit" className="primary" disabled={salvar.saving || motivo.trim().length < 3}>
              {salvar.saving ? 'Salvando…' : 'Salvar configuração'}
            </button>
          </form>
        </Card>
      ) : null}

      {editavel && !semProvedor ? <ConsoleDeTeste /> : null}
    </>
  );
}

function ConsoleDeTeste() {
  const [mensagem, setMensagem] = useState('');
  const [resposta, setResposta] = useState<ResultadoDoTeste | null>(null);

  const testar = useSubmit(async () => {
    const r = await post<ResultadoDoTeste>('/api/ai/test', { mensagem });
    setResposta(r);
    setMensagem('');
  });

  const reiniciar = useSubmit(async () => {
    await post('/api/ai/test', { mensagem: 'olá', reiniciar: true });
    setResposta(null);
  });

  return (
    <Card
      title="Testar conversa"
      description="Nada é enviado para o WhatsApp. Serve para ver o que o assistente responderia."
      actions={
        <button type="button" className="small" onClick={() => void reiniciar.submit()}>
          Recomeçar
        </button>
      }
    >
      <form onSubmit={testar.submit}>
        <ErrorBox error={testar.error} />
        <Field label="Mensagem do cliente">
          <input
            type="text"
            value={mensagem}
            onChange={(e) => setMensagem(e.target.value)}
            placeholder="Ex.: bom dia, quanto fica a troca de óleo do meu Argo?"
          />
        </Field>
        <button type="submit" className="primary" disabled={testar.saving || !mensagem.trim()}>
          {testar.saving ? 'Testando…' : 'Testar'}
        </button>
      </form>

      {resposta ? (
        <div style={{ marginTop: 16 }}>
          <p>
            <Badge tone={DESFECHO[resposta.resultado.desfecho]?.tom ?? 'neutral'}>
              {DESFECHO[resposta.resultado.desfecho]?.rotulo ?? resposta.resultado.desfecho}
            </Badge>{' '}
            <span className="muted small">
              custo estimado {formatBRL(resposta.resultado.custoCentavos)}
            </span>
          </p>

          {resposta.resultado.detalhe ? (
            <Alert tone="warning" title="Motivo">
              {resposta.resultado.detalhe}
            </Alert>
          ) : null}

          {resposta.resultado.chamadasDeFerramenta.length > 0 ? (
            <>
              <h3 style={{ margin: '12px 0 6px' }}>Dados consultados</h3>
              <ul className="list" style={{ border: '1px solid var(--gl-border)', borderRadius: 6 }}>
                {resposta.resultado.chamadasDeFerramenta.map((c, i) => (
                  <li key={i}>
                    <div className="list-main">
                      <code>{c.nome}</code>
                    </div>
                    <Badge tone={c.ok ? 'ok' : 'critical'}>{c.ok ? 'ok' : 'recusado'}</Badge>
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          {resposta.resultado.textoEnviado ? (
            <>
              <h3 style={{ margin: '12px 0 6px' }}>Resposta que sairia</h3>
              <div className="preview-box">{resposta.resultado.textoEnviado}</div>
            </>
          ) : null}

          <p className="hint" style={{ marginTop: 10 }}>
            {resposta.aviso}
          </p>
        </div>
      ) : null}
    </Card>
  );
}
